import { useState, useEffect, FormEvent, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE, apiGet, uploadImage, uploadPDF, getAdminToken } from '../utils/api';
import type { PostData } from '../hooks/usePages';
import YouTubeEmbed from '../components/YouTubeEmbed';
import ReactQuill from 'react-quill';

interface PostFormData {
  id: string;
  slug: string;
  titleHe: string;
  titleEn: string;
  excerptHe: string;
  excerptEn: string;
  contentHe: string;
  contentEn: string;
  category: string;
  parentPage: string;
  author: string;
  date: string;
  videos: string[];
  images: string[];
  files: { name: string; path: string }[];
  imageDisplayMode: 'gallery' | 'carousel';
}

const CATEGORIES = [
  { value: 'survivors', label: 'סיפורי ניצולים / Survivor Stories', parentPage: 'survivor-stories' },
  { value: 'people', label: 'אישים / Famous People', parentPage: 'famous-people' },
  { value: 'events', label: 'אירועים / Events', parentPage: 'events' },
  { value: 'news', label: 'חדשות / News', parentPage: 'community-news' },
  { value: 'content', label: 'תוכן כללי / General Content', parentPage: '' },
];

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, false] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'indent', 'direction', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video',
];

export default function AdminPostEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isCopy = searchParams.get('copy') === 'true';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const imageInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [videoInput, setVideoInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  const [form, setForm] = useState<PostFormData>({
    id: '',
    slug: '',
    titleHe: '',
    titleEn: '',
    excerptHe: '',
    excerptEn: '',
    contentHe: '',
    contentEn: '',
    category: 'content',
    parentPage: '',
    author: 'admin',
    date: new Date().toISOString().split('T')[0],
    videos: [],
    images: [],
    files: [],
    imageDisplayMode: 'gallery',
  });

  const loadExisting = useCallback(async (postId: string) => {
    try {
      const post = await apiGet<PostData>(`/posts/${postId}`);
      if (post) {
        const newForm = {
          id: post.id,
          slug: post.slug,
          titleHe: post.title?.he || '',
          titleEn: post.title?.en || '',
          excerptHe: post.excerpt?.he || '',
          excerptEn: post.excerpt?.en || '',
          contentHe: post.content?.he || '',
          contentEn: post.content?.en || '',
          category: post.category || 'content',
          parentPage: post.parentPage || '',
          author: post.author || 'admin',
          date: post.date || new Date().toISOString().split('T')[0],
          videos: post.videos || [],
          images: post.images || [],
          files: post.files || [],
          imageDisplayMode: post.imageDisplayMode || 'gallery',
        };
        setForm(newForm);
        setIsLoaded(true);
      } else {
        setMessage('Post not found');
      }
    } catch (err) {
      console.error('Error loading post:', err);
      setMessage(`Error loading post: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, []);

  useEffect(() => {
    if (id && !isLoaded) {
      loadExisting(id);
    }
  }, [id, loadExisting]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (value: string, lang: 'he' | 'en') => {
    setForm((prev) => {
      const updated = { ...prev, [lang === 'he' ? 'titleHe' : 'titleEn']: value };
      if (!id) {
        const slugSource = lang === 'en' ? value : prev.titleEn || value;
        const slug = generateSlug(slugSource);
        updated.slug = slug;
        updated.id = slug;
      }
      return updated;
    });
  };

  const handleCategoryChange = (category: string) => {
    const cat = CATEGORIES.find((c) => c.value === category);
    setForm((prev) => ({
      ...prev,
      category,
      parentPage: cat?.parentPage || '',
    }));
  };

  const addVideo = () => {
    if (videoInput.trim()) {
      setForm((prev) => ({ ...prev, videos: [...prev.videos, videoInput.trim()] }));
      setVideoInput('');
    }
  };

  const removeVideo = (index: number) => {
    setForm((prev) => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = getAdminToken()!;
    for (const file of Array.from(files)) {
      try {
        const result = await uploadImage(file, token);
        setForm((prev) => ({ ...prev, images: [...prev.images, result.path] }));
      } catch {
        alert(`Failed to upload: ${file.name}`);
      }
    }
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handlePDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = getAdminToken()!;
    if (!form.slug) {
      alert('Please set a post slug before uploading files');
      return;
    }
    for (const file of Array.from(files)) {
      try {
        const result = await uploadPDF(file, token, form.slug);
        setForm((prev) => ({
          ...prev,
          files: [
            ...prev.files,
            {
              name: file.name.replace(/\.[^.]+$/, ''), // Remove extension from name
              path: result.path,
            },
          ],
        }));
      } catch {
        alert(`Failed to upload: ${file.name}`);
      }
    }
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const updateFileLabel = (index: number, name: string) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.map((f, i) => (i === index ? { ...f, name } : f)),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const token = getAdminToken()!;
    const postData = {
      id: form.id,
      slug: form.slug,
      title: { he: form.titleHe, en: form.titleEn },
      category: form.category,
      parentPage: form.parentPage,
      date: form.date,
      author: form.author,
      excerpt: { he: form.excerptHe, en: form.excerptEn },
      content: { he: form.contentHe, en: form.contentEn },
      images: form.images,
      videos: form.videos,
      files: form.files,
      imageDisplayMode: form.imageDisplayMode,
    };

    try {
      const slugChanged = isCopy && form.slug !== id;
      const url = id && !slugChanged
        ? `${API_BASE}/admin/posts/${id}`
        : `${API_BASE}/admin/posts`;
      const method = id && !slugChanged ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(postData),
      });

      if (res.ok) {
        // If slug changed on a copy, delete the old copy
        if (slugChanged && id) {
          await fetch(`${API_BASE}/admin/posts/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        setMessage(t('admin.saved'));
        setTimeout(() => navigate('/admin/dashboard'), 1500);
      } else {
        setMessage('Error saving');
      }
    } catch {
      setMessage('Error saving');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{id ? 'עריכת פוסט / Edit Post' : 'פוסט חדש / New Post'}</h1>
        <button onClick={() => navigate('/admin/dashboard')} className="btn btn-secondary">
          {t('admin.cancel')}
        </button>
      </div>

      <form className="admin-editor-form" onSubmit={handleSubmit}>
        {/* Titles */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('admin.titleHe')}</label>
            <input
              className="form-input"
              value={form.titleHe}
              onChange={(e) => handleTitleChange(e.target.value, 'he')}
              required
              dir="rtl"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('admin.titleEn')}</label>
            <input
              className="form-input"
              value={form.titleEn}
              onChange={(e) => handleTitleChange(e.target.value, 'en')}
              dir="ltr"
            />
          </div>
        </div>

        {/* Slug, Category, Date, Author */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Slug / ID {id && !isCopy && <span style={{ fontSize: '0.85em', color: 'var(--color-text-muted)' }}>(read-only)</span>}
            </label>
            <input
              className="form-input"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value, id: e.target.value }))}
              dir="ltr"
              disabled={!!id && !isCopy}
              title={id && !isCopy ? 'Slug cannot be changed when editing existing posts' : 'Auto-generated from English title'}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('admin.category')}</label>
            <select
              className="form-select"
              value={form.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Date</label>
            <input
              className="form-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Author</label>
            <input
              className="form-input"
              value={form.author}
              onChange={(e) => setForm((prev) => ({ ...prev, author: e.target.value }))}
              dir="ltr"
            />
          </div>
        </div>

        {/* Excerpts */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('admin.excerptHe')}</label>
            <textarea
              className="form-textarea"
              value={form.excerptHe}
              onChange={(e) => setForm((prev) => ({ ...prev, excerptHe: e.target.value }))}
              rows={2}
              dir="rtl"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('admin.excerptEn')}</label>
            <textarea
              className="form-textarea"
              value={form.excerptEn}
              onChange={(e) => setForm((prev) => ({ ...prev, excerptEn: e.target.value }))}
              rows={2}
              dir="ltr"
            />
          </div>
        </div>

        {/* Content */}
        <div className="form-group">
          <label className="form-label">{t('admin.contentHe')}</label>
          <ReactQuill
            value={form.contentHe}
            onChange={(value) => setForm((prev) => ({ ...prev, contentHe: value }))}
            theme="snow"
            modules={quillModules}
            formats={quillFormats}
            placeholder="ניתן להשתמש ב-HTML..."
            style={{ direction: 'rtl', textAlign: 'right' }}
          />
        </div>
        <div className="form-group">
          <label className="form-label">{t('admin.contentEn')}</label>
          <ReactQuill
            value={form.contentEn}
            onChange={(value) => setForm((prev) => ({ ...prev, contentEn: value }))}
            theme="snow"
            modules={quillModules}
            formats={quillFormats}
            placeholder="HTML is supported..."
            style={{ direction: 'ltr', textAlign: 'left' }}
          />
        </div>

        {/* Videos */}
        <div className="form-group">
          <label className="form-label">{t('admin.youtubeUrl')}</label>
          <div className="input-with-button">
            <input
              className="form-input"
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              dir="ltr"
            />
            <button type="button" className="btn btn-secondary" onClick={addVideo}>
              {t('admin.addVideo')}
            </button>
          </div>
          {form.videos.length > 0 && (
            <div className="video-list">
              {form.videos.map((url, i) => (
                <div key={i} className="video-item">
                  <YouTubeEmbed url={url} />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeVideo(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image display mode */}
        <div className="form-group">
          <label className="form-label">תצוגת תמונות / Image Display</label>
          <select
            className="form-select"
            value={form.imageDisplayMode}
            onChange={(e) => setForm((prev) => ({ ...prev, imageDisplayMode: e.target.value as 'gallery' | 'carousel' }))}
          >
            <option value="gallery">גלריה (רשת) / Gallery (Grid)</option>
            <option value="carousel">קרוסלה / Carousel (Slider)</option>
          </select>
        </div>

        {/* Images */}
        <div className="form-group">
          <label className="form-label">{t('admin.uploadImage')}</label>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => imageInputRef.current?.click()}
          >
            {t('admin.uploadImage')}
          </button>
          {form.images.length > 0 && (
            <div className="image-preview-list">
              {form.images.map((src, i) => (
                <div key={i} className="image-preview-item">
                  <img src={src} alt={`Upload ${i + 1}`} />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeImage(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PDF Files */}
        <div className="form-group">
          <label className="form-label">העלאת קבצים / Upload PDF Files</label>
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf,.pptx,.ppt,.mp4,.webm,.ogg,.wav,.mp3"
            multiple
            onChange={handlePDFUpload}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => pdfInputRef.current?.click()}
          >
            העלאת קבצים / Upload Files
          </button>
          {form.files.length > 0 && (
            <div className="file-list">
              {form.files.map((file, i) => (
                <div key={i} className="file-item">
                  <div className="file-info">
                    <input
                      type="text"
                      className="file-label-input"
                      value={file.name}
                      onChange={(e) => updateFileLabel(i, e.target.value)}
                      placeholder="תווית הקובץ / File label"
                    />
                    <span className="file-path" dir="ltr">{file.path}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeFile(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '...' : t('admin.save')}
          </button>
          {message && <span className="form-message">{message}</span>}
        </div>
      </form>
    </div>
  );
}
